/* NotificationCenter — zvoneček + panel připomínek (v horní liště, GlobalTabBar).
 *
 * Vše se počítá CLIENT-SIDE z existujících endpointů (žádná backend změna → jede hned):
 *   • /api/crm/tasks       → úkoly (termín `deadline`, příznak `done`, `contact_id`)
 *   • /api/crm/contacts    → jména klientů + narozeniny / výročí / příští servis
 *   • /api/contracts/upcoming-fixations → konce fixací / dožití / výročí smluv
 *
 * Skupiny:
 *   • Po termínu   — úkoly s termínem < dnes, nehotové (červené)
 *   • Dnes         — úkoly s termínem dnes
 *   • Rezervace    — BUDOUCÍ schůzka, kterou si klient sám zarezervoval na
 *                    /book/:slug (components/notif/rezervaceNotif.js). Druhá
 *                    výjimka z pravidla „budoucnost do zvonečku nepatří":
 *                    o rezervaci se poradce jinak nedozví vůbec a slíbí ten
 *                    čas někomu dalšímu. Nová rezervace rozsvítí odznak i
 *                    tehdy, když si poradce panel dnes už přečetl.
 *   • Týmová akce  — BUDOUCÍ porada / teambuilding / školení od manažera
 *                    (components/notif/akceNotif.js). Spolu s rezervacemi
 *                    jediné dvě skupiny, které smí ukázat budoucnost — a jen
 *                    zrcadla týmových akcí, ne celý diář (jinak by zvoneček
 *                    zahltil každý plný kalendář). Kdy naskočí, říká
 *                    `pripominka_dny` z akce.
 *   • Blíží se     — SERVISNÍ RADAR (jeden zdroj pravdy: lib/servisRadar.js) —
 *                    servis / výročí / narozeniny, seřazené dle nejbližšího termínu.
 *   • Co vidí tvůj manažer — JEDNORÁZOVÁ věta pro stávající členy týmu, kteří
 *                    se přidali před Výkonem týmu (components/notif/
 *                    informaceNotif.js). Po zobrazení se zapíše na vztah
 *                    a podruhé se už neukáže (majitel 12. 10. 2026).
 *
 * Odznak (badge) = počet nepřečtených. Denní model: dokud poradce panel dnes
 *   neotevřel (lastSeen < začátek dneška), odznak ukazuje počet aktuálních
 *   připomínek (po termínu + dnes + servis do 14 dní). Otevření panelu = přečteno
 *   (uloží „naposledy viděno" do localStorage per uživatel) → odznak zmizí.
 */

/* BARVY = TOKENY MOTIVU, NE ZAMRAŽENÉ HEXY (16. 9. 2026).
 * Tahle obrazovka běží v aplikaci, takže se musí přepnout spolu s motivem.
 * Do téhle chvíle měla kopii palety Sage, takže v Cobaltu i Midnight zůstávala
 * béžová. (Tiskové výstupy — OnePagePlanPage, AnalyzaTiskPage — si pevné barvy
 * drží schválně: papír motiv nemá.)
 * ⚠️ Značkové barvy cizích firem (WhatsApp #25D366) se NEPŘEPISUJÍ. */
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, X, ChevronRight,
  ClipboardList, Users, CalendarRange, CalendarPlus, Mail, Phone, FileText,
  Briefcase, Award, Wrench, Cake, BellOff, Eye,
} from "lucide-react";
import PrazdnyStav from "@/components/ikony/PrazdnyStav";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { listTasks, listContacts, upcomingFixations, myRelationship, potvrditInformaciManazera } from "@/lib/crmApi";
import { computeServisRadar, dnyDo } from "@/lib/servisRadar";
/* Komunikace (1. 11. 2026): jeden řádek „Dnes: 3 narozeniny, 1 fixace" → /crm/komunikace.
   Počítá se ze stejných dat (kontakty, smlouvy, diář), žádné další volání. */
import { udalostiKomunikace, shrnutiDne } from "@/lib/komunikace/udalosti";
import { sanitizeList } from "@/components/notif/notifLists";
import { nadchazejiciAkce, barvaDruhu } from "@/components/notif/akceNotif";
import { cekajiciPozvanky } from "@/components/notif/pozvankaNotif";
import { informaceKZobrazeni, zapisMaSmyslOpakovat, INFORMACE_VERZE } from "@/components/notif/informaceNotif";
import { nadchazejiciRezervace, rezervaceProOdznak } from "@/components/notif/rezervaceNotif";
/* Nové leady z webu (18. 10. 2026) — stejný výpočet jako „Nové leady" ve Zrcadle volání. */
import { noveLeady as spocitejNoveLeady, prisel as leadPrisel } from "@/lib/crm/noveLeady";
import { popisZdroje } from "@/lib/crm/puvodLeadu";
import { diarFocusSearch } from "@/lib/notifDiarFocus";
import { navigateOverlaySafe } from "@/lib/paletteNav";
import { openClientFolder } from "@/lib/openClientFolder";
import { TL } from "@/lib/prekladyUI";

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const BADGE_SOON_DAYS = 14;   // do kolika dní servisní radar započítat do odznaku
const RADAR_SHOWN = 12;       // kolik servisních položek vypsat v panelu (zbytek → radar)

/* Locale pro Intl (formát data a času). Mapa, ne ternár: `lang === "sk" ? … : …`
   leželo v souboru pětkrát a ráčna nad dvoujazyčnými ternáry (jazykBezCestiny)
   je počítá jako místa, která znají jen dva jazyky. Tohle jich pět ubere a
   přidání dalšího jazyka je pak jeden řádek, ne pět. */
const LOCALE = { sk: "sk-SK", cs: "cs-CZ" };
const locale = (lang) => LOCALE[lang] || LOCALE.cs;

// Relativní čas dne (celé dny): dnes / zítra / za 3 dny / před 2 dny.
function relLabel(days, lang) {
  const sk = lang === "sk";
  if (days === 0) return "dnes";
  if (days === 1) return TL("zítra", lang);
  if (days === -1) return TL("včera", lang);
  if (days > 1) return sk ? `o ${days} ${days < 5 ? "dni" : "dní"}` : `za ${days} ${days < 5 ? "dny" : "dní"}`;
  const a = Math.abs(days);
  return sk ? `pred ${a} ${a < 5 ? "dňami" : "dňami"}` : `před ${a} dny`;
}

// Ikona + typ úkolu (fallback z textu, když activity_type chybí).
const TASK_ICON = { ukol: ClipboardList, schuzka: Users, udalost: CalendarRange, email: Mail, telefonat: Phone, dopis: FileText };
function taskType(tk) {
  if (tk.activity_type && TASK_ICON[tk.activity_type]) return tk.activity_type;
  const s = (tk.text || "").toLowerCase();
  if (/volat|zavolej|zavolať|telef|hovor|callback/.test(s)) return "telefonat";
  if (/schůz|stretnut|schuzk/.test(s)) return "schuzka";
  if (/email|mail|nabídk|ponuk/.test(s)) return "email";
  if (/dopis|list|poštou/.test(s)) return "dopis";
  return "ukol";
}

// Servisní radar — ikona + barva dle druhu signálu (sladěno se ServisRadarPage).
const RADAR_ICON = { refix: Briefcase, konec: Briefcase, vyroci: Award, servis: Wrench, vyroci_klient: Award, narozeniny: Cake };
const RADAR_COLOR = { refix: "var(--accent)", konec: "var(--accent)", vyroci: "var(--positive)", vyroci_klient: "var(--positive)", servis: "var(--accent)", narozeniny: "var(--info)" };

const TXT = {
  cs: {
    aria: "Připomínky", title: "Připomínky",
    overdue: "Po termínu", today: "Dnes", soon: "Blíží se",
    akce: "Týmová akce", rezervace: "Nová rezervace", noveLeady: "Nové leady",
    empty: "Žádné připomínky — máš čisto.",
    openRadar: "Otevřít servisní radar", more: "dalších",
    task: "Úkol",
    pozvanka: "Pozvánka do týmu", pozvankaOd: "Zve vás {kdo}",
    pozvankaBezJmena: "Pozvánka od manažera", pozvankaVyprsi: "vyprší za {n} dní",
    pozvankaDnes: "vyprší dnes", pozvankaVyridit: "Vyřídit v Nastavení",
  },
  sk: {
    aria: "Pripomienky", title: "Pripomienky",
    overdue: "Po termíne", today: "Dnes", soon: "Blíži sa",
    akce: "Tímová akcia", rezervace: "Nová rezervácia", noveLeady: "Nové leady",
    empty: "Žiadne pripomienky — máš čisto.",
    openRadar: "Otvoriť servisný radar", more: "ďalších",
    task: "Úloha",
    pozvanka: "Pozvánka do tímu", pozvankaOd: "Pozýva vás {kdo}",
    pozvankaBezJmena: "Pozvánka od manažéra", pozvankaVyprsi: "vyprší o {n} dní",
    pozvankaDnes: "vyprší dnes", pozvankaVyridit: "Vybaviť v Nastaveniach",
  },
  en: {
    aria: "Reminders", title: "Reminders",
    overdue: "Overdue", today: "Today", soon: "Coming up",
    akce: "Team event", rezervace: "New booking", noveLeady: "New leads",
    empty: "No reminders — all clear.",
    openRadar: "Open the service radar", more: "more",
    task: "Task",
    pozvanka: "Team invitation", pozvankaOd: "{kdo} is inviting you",
    pozvankaBezJmena: "Invitation from a manager", pozvankaVyprsi: "expires in {n} days",
    pozvankaDnes: "expires today", pozvankaVyridit: "Handle it in Settings",
  },
  uk: {
    aria: "Нагадування", title: "Нагадування",
    overdue: "Прострочено", today: "Сьогодні", soon: "Наближається",
    akce: "Командний захід", rezervace: "Нове бронювання", noveLeady: "Нові ліди",
    empty: "Немає нагадувань — усе чисто.",
    openRadar: "Відкрити сервісний радар", more: "ще",
    task: "Завдання",
    pozvanka: "Запрошення до команди", pozvankaOd: "Вас запрошує {kdo}",
    pozvankaBezJmena: "Запрошення від менеджера", pozvankaVyprsi: "спливає через {n} дн.",
    pozvankaDnes: "спливає сьогодні", pozvankaVyridit: "Опрацювати в Налаштуваннях",
  },
  vi: {
    aria: "Nhắc việc", title: "Nhắc việc",
    overdue: "Quá hạn", today: "Hôm nay", soon: "Sắp tới",
    akce: "Sự kiện nhóm", rezervace: "Lịch hẹn mới", noveLeady: "Khách tiềm năng mới",
    empty: "Không có nhắc việc — mọi thứ đã xong.",
    openRadar: "Mở radar dịch vụ", more: "nữa",
    task: "Công việc",
    pozvanka: "Lời mời vào nhóm", pozvankaOd: "{kdo} mời bạn",
    pozvankaBezJmena: "Lời mời từ quản lý", pozvankaVyprsi: "hết hạn sau {n} ngày",
    pozvankaDnes: "hết hạn hôm nay", pozvankaVyridit: "Xử lý trong Cài đặt",
  },
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  /* Zvoneček uměl do 9. 9. 2026 jen `cs` a `sk` — vietnamský poradce viděl
   * připomínky česky. Neznámý jazyk padá na češtinu, ne na prázdno. */
  const T = TXT[lang] || TXT.cs;

  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [fixations, setFixations] = useState([]);
  const [pozvanky, setPozvanky] = useState([]);   // čekající pozvánky do týmu
  /* Jednorázová věta „Co vidí tvůj manažer" (informaceNotif.js).
   *   informaceChybi   server říká: potvrzený vztah, věta ještě nezapsaná
   *   informaceVPanelu věta se v TOMHLE otevření panelu ukázala — zůstane
   *                    vidět až do zavření, i když ji mezitím obnova ze
   *                    serveru už hlásí jako zapsanou
   *   informaceSkryta  poradce klikl „Rozumím" (jen do zavření panelu) */
  const [informaceChybi, setInformaceChybi] = useState(false);
  const [informaceVPanelu, setInformaceVPanelu] = useState(false);
  const [informaceSkryta, setInformaceSkryta] = useState(false);
  const informaceZapisujeRef = useRef(false);
  const informaceVzdanaRef = useRef(false);   // 404/409: opakování nic nespraví
  const [loadFailed, setLoadFailed] = useState(false); // chyba načtení ≠ „máš čisto"

  const seenKey = `bpu_notif_seen:${user?.id || user?.email || "anon"}`;
  const [lastSeen, setLastSeen] = useState(0);

  const wrapRef = useRef(null);
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  /* PANEL NESMÍ UTÉCT Z OBRAZOVKY (snímky opravné dávky 14. 10. 2026): panel
   * je zarovnaný k pravému okraji zvonečku, který na mobilu NENÍ u pravého
   * okraje okna — při 390 px začínal na x = −51 a nadpis i blok „Co vidí
   * tvůj manažer“ byly vlevo uříznuté. Po otevření se změří a posune tak,
   * aby držel 12 px od obou okrajů okna. */
  const [posunPanelu, setPosunPanelu] = useState(0);
  useLayoutEffect(() => {
    if (!open) { setPosunPanelu(0); return; }
    const el = panelRef.current;
    if (!el || typeof window === "undefined" || typeof el.getBoundingClientRect !== "function") return;
    const r = el.getBoundingClientRect();
    const OKRAJ = 12;
    let s = 0;
    if (r.left < OKRAJ) s = OKRAJ - r.left;
    else if (r.right > window.innerWidth - OKRAJ) s = window.innerWidth - OKRAJ - r.right;
    if (Math.abs(s) >= 1) setPosunPanelu((p) => p + Math.round(s));
  }, [open]);

  // Načíst „naposledy viděno" (per uživatel) — i při přepnutí účtu.
  useEffect(() => {
    try { setLastSeen(Number(localStorage.getItem(seenKey) || 0)); } catch { setLastSeen(0); }
  }, [seenKey]);

  const load = useCallback(async () => {
    try {
      // Sleduj selhání zdrojů: prázdný zvoneček po CHYBĚ nesmí hlásit „máš čisto".
      let failed = 0;
      const [tk, ct, fx, rel] = await Promise.all([
        listTasks().catch(() => { failed++; return []; }),
        listContacts().catch(() => { failed++; return []; }),
        upcomingFixations(120).catch(() => { failed++; return []; }),
        /* Pozvánka do týmu. Vlastní catch a ZÁMĚRNĚ bez `failed++`: kdo žádného
         * manažera nemá, dostane tu prázdno úplně běžně — hlásit kvůli tomu
         * „načtení selhalo" by bylo falešné. */
        myRelationship().catch(() => null),
      ]);
      setTasks(sanitizeList(tk));
      setContacts(sanitizeList(ct));
      setFixations(sanitizeList(fx));
      setPozvanky(sanitizeList((rel && rel.invitations) || []));
      /* Chyba načtení vztahu (rel = null) = NEVÍME → věta se neukáže. */
      setInformaceChybi(!informaceVzdanaRef.current && informaceKZobrazeni(rel));
      setLoadFailed(failed > 0);
    } catch { setLoadFailed(true); /* síť nikdy neshodí zvoneček */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Kancelář bývá otevřená celý den → jemné periodické obnovení.
  useEffect(() => { const iv = setInterval(load, 4 * 60000); return () => clearInterval(iv); }, [load]);

  // Mapa contact_id → celé jméno (úkoly nesou jen contact_id).
  const contactName = useMemo(() => {
    const m = new Map();
    (contacts || []).forEach((c) => { if (c && c.id) m.set(c.id, [c.name, c.surname].filter(Boolean).join(" ").trim() || c.company || ""); });
    return m;
  }, [contacts]);

  // Skupiny.
  const { overdue, todayList, blizi, akce, rezervace, pozvankyProPanel, noveL } = useMemo(() => {
    const ov = [], td = [];
    (tasks || []).forEach((tk) => {
      if (tk.done) return;
      const iso = tk.deadline || tk.start_iso;
      if (!iso) return;
      const when = new Date(iso);
      if (isNaN(when.getTime())) return;
      const dd = dnyDo(iso);
      if (dd > 0) return; // budoucí úkoly do zvonečku nepatří (jen po termínu / dnes)
      const item = { id: tk.id, task: tk, days: dd, when, name: tk.contact_name || contactName.get(tk.contact_id) || "" };
      if (dd < 0) ov.push(item); else td.push(item);
    });
    ov.sort((a, b) => a.when - b.when);
    td.sort((a, b) => a.when - b.when);
    const radar = computeServisRadar({ fixations, contacts, lang });
    // Týmové akce jsou jediná výjimka z pravidla „budoucnost do zvonečku nepatří" —
    // porada, o které se poradce dozví až ráno v den konání, je k ničemu. Filtruje
    // se ze stejného pole `tasks`, takže žádné další volání na server.
    const akceList = nadchazejiciAkce(tasks || []);
    // Druhá výjimka: online rezervace. Klient si vzal čas z kalendáře poradce,
    // aniž by o tom poradce věděl — proto se hlásí dopředu, ne až v den schůzky.
    const rezervaceList = nadchazejiciRezervace(tasks || []);
    /* Třetí výjimka z „budoucnost sem nepatří": pozvánka do týmu. Poradce se
     * o ní jinak dozví jen tak, že náhodou zajde dolů do Nastavení — a do té
     * doby mu mlčky vyprší (viz components/notif/pozvankaNotif.js). */
    const pozvankyList = cekajiciPozvanky(pozvanky || []);
    return { overdue: ov, todayList: td, blizi: radar, akce: akceList, rezervace: rezervaceList, pozvankyProPanel: pozvankyList, noveL: spocitejNoveLeady(contacts || []) };
  }, [tasks, contacts, fixations, contactName, lang, pozvanky]);

  const ukazInformaci = open && !informaceSkryta && (informaceChybi || informaceVPanelu);
  const total = overdue.length + todayList.length + blizi.length + akce.length + rezervace.length + pozvankyProPanel.length + noveL.length
    + (ukazInformaci ? 1 : 0);

  // Odznak — denní model (viz hlavička souboru).
  const startToday = startOfDay(new Date()).getTime();
  const badgeSet = overdue.length + todayList.length + akce.length + rezervace.length + blizi.filter((r) => r.days <= BADGE_SOON_DAYS).length;
  // Výjimka z denního modelu: rezervace, která přišla AŽ POTOM, co si poradce
  // dnes zvoneček přečetl, musí odznak rozsvítit znovu. Jinak by se o zabraném
  // termínu dozvěděl nejdřív zítra — a do té doby ho slíbil někomu jinému.
  // Počítá se z ÚKOLŮ, ne z panelu: panel dnešek vynechává (patří do skupiny
  // „Dnes"), jenže rezervace NA DNES je ta nejnaléhavější — u poradce s krátkou
  // výpovědní lhůtou by jinak odznak zůstal zhasnutý až do zítřka.
  const noveRez = rezervaceProOdznak(tasks || [], lastSeen);
  /* Pozvánka se přičítá VŽDY, mimo denní model: ostatní připomínky jsou zprávy
   * a zhasnou přečtením, pozvánka je ÚKOL — dokud ji poradce nepřijme nebo
   * neodmítne, je pořád co dělat a mlčky vyprší. */
  /* Věta „Co vidí tvůj manažer" se přičítá taky mimo denní model — dokud se
   * jednou nezobrazí, nemá zhasnout jen proto, že dnes už panel někdo otevřel. */
  /* Nový lead rozsvítí odznak, když přišel po posledním otevření panelu — jako rezervace. */
  const unread = (lastSeen < startToday ? badgeSet : noveRez.length) + pozvankyProPanel.length
    + noveL.filter((c) => leadPrisel(c) > lastSeen).length
    + (informaceChybi ? 1 : 0);

  const markSeen = useCallback(() => {
    const now = Date.now();
    setLastSeen(now);
    try { localStorage.setItem(seenKey, String(now)); } catch { /* soukromý režim */ }
  }, [seenKey]);

  const togglePanel = () => {
    setOpen((o) => {
      const next = !o;
      if (next) { load(); markSeen(); }
      return next;
    });
  };

  /* Věta o tom, co vidí manažer, se zapíše HNED PO ZOBRAZENÍ (otevřený panel
   * s větou nahoře) — „jednou oznámit" znamená ukázat, ne čekat na klik.
   * Selže-li síť, zůstane svítit a zkusí se při příštím otevření. */
  const zapisInformaci = useCallback(() => {
    if (informaceZapisujeRef.current) return;
    informaceZapisujeRef.current = true;
    Promise.resolve()
      .then(() => potvrditInformaciManazera(INFORMACE_VERZE))
      .then(() => setInformaceChybi(false))
      .catch((e) => {
        if (!zapisMaSmyslOpakovat(e)) { informaceVzdanaRef.current = true; setInformaceChybi(false); }
      })
      .finally(() => { informaceZapisujeRef.current = false; });
  }, []);

  useEffect(() => {
    if (!open) { setInformaceVPanelu(false); setInformaceSkryta(false); return; }
    if (!informaceChybi) return;
    setInformaceVPanelu(true);
    zapisInformaci();
  }, [open, informaceChybi, zapisInformaci]);

  // Klik mimo + Esc + focus panelu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); bellRef.current?.focus?.(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    const tid = setTimeout(() => panelRef.current?.focus?.(), 0);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); clearTimeout(tid); };
  }, [open]);

  // Klik na úkol/událost → diář skočí přímo na den + zvýrazní položku.
  // Předáme cíl přes query (?date=&task=&view=day) — přežije i reload, na rozdíl
  // od location.state. Když z položky nejde odvodit den, jdeme na holý /diar (dnešek).
  const goTask = (it) => {
    setOpen(false);
    const search = it && it.task ? diarFocusSearch(it.task) : null;
    // Overlay-safe: bez zavření otevřené klientské složky by diář naskočil POD ni
    // a poradci by to vypadalo, že klik na notifikaci nic neudělal.
    navigateOverlaySafe(navigate, search ? `/diar${search}` : "/diar");
  };
  // Servisní/fixační notifikace míří rovnou na Smlouvy; narozeniny/výročí klienta na Kartu.
  const goClient = (it) => {
    const cid = it && typeof it === "object" ? it.contact_id : it;
    if (!cid) return;
    setOpen(false);
    const kind = it && typeof it === "object" ? it.kind : null;
    const section = kind === "narozeniny" || kind === "vyroci_klient" ? "karta" : "smlouvy";
    openClientFolder({ contactId: cid, section, source: "zvonecek" });
  };

  const chipIcon = (color) => ({ background: `color-mix(in srgb, ${color} 15%, transparent)`, color });
  const timeChip = (days) => {
    const danger = days < 0;
    const warm = days >= 0 && days <= 7;
    const col = danger ? "var(--cat-c)" : warm ? "var(--accent)" : "var(--muted)";
    return { color: col, background: `color-mix(in srgb, ${danger ? "var(--cat-c)" : warm ? "var(--accent)" : "var(--muted)"} 12%, transparent)` };
  };

  const radarShown = blizi.slice(0, RADAR_SHOWN);
  const radarRest = blizi.length - radarShown.length;
  // Komunikace: shrnutí dneška jednou větou (prázdné = řádek se nevykreslí).
  const komunikaceDnes = useMemo(() => {
    try { return shrnutiDne(udalostiKomunikace({ contacts, fixations, tasks }), lang); } catch { return ""; }
  }, [contacts, fixations, tasks, lang]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={bellRef}
        onClick={togglePanel}
        data-testid="notif-bell"
        aria-label={T.aria}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={T.aria}
        className="relative inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors"
        style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)", boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))" }}
      >
        <Bell size={15} style={{ color: unread > 0 ? "var(--accent)" : "var(--muted)" }} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            /* `t-badge-dot` (index.css, transitions-dev): tečka naskočí ze scale 0,6 za 250 ms, zvonek stojí. */
            className="absolute flex items-center justify-center font-bold tabular-nums t-badge-dot"
            style={{
              top: -4, right: -4, minWidth: 16, height: 16, padding: "0 4px",
              borderRadius: 999, fontSize: 9.5, lineHeight: 1,
              background: "var(--cat-c)", color: "#fff", border: "2px solid var(--bg)",
            }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={T.title}
          tabIndex={-1}
          data-testid="notif-panel"
          className="absolute right-0 mt-2 rounded-xl overflow-hidden outline-none"
          style={{
            top: "100%",
            right: posunPanelu ? `${-posunPanelu}px` : undefined,
            width: "min(360px, calc(100vw - 24px))",
            maxHeight: "70vh",
            display: "flex", flexDirection: "column",
            background: "var(--surface)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow-float, 0 24px 60px rgba(38,34,26,0.3))",
            zIndex: 130,
          }}
        >
          {/* Hlavička */}
          <div className="flex items-center gap-2 px-3.5 py-2.5 flex-shrink-0" style={{ borderBottom: "1px solid var(--line)" }}>
            <Bell size={14} style={{ color: "var(--accent)" }} />
            <span className="text-[13px] font-bold flex-1" style={{ color: "var(--ink)" }}>{T.title}</span>
            {total > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>{total}</span>
            )}
            <button onClick={() => { setOpen(false); bellRef.current?.focus?.(); }} data-testid="notif-close" aria-label="Zavřít" className="w-6 h-6 rounded-full flex items-center justify-center" style={{ color: "var(--muted)" }}>
              <X size={14} />
            </button>
          </div>

          {/* Tělo */}
          <div className="overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            {total === 0 && loadFailed ? (
              <div className="px-4 py-10 text-center text-[12.5px]" style={{ color: "var(--cat-c)" }} data-testid="notif-load-error">
                {t("auto.notificationCenter.pripominkySeNepodariloNacist")}
                <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
                  {t("auto.notificationCenter.tohleNeniStavMas")}
                </div>
              </div>
            ) : total === 0 ? (
              /* Prázdno bez oslavy (30. 9. 2026): tlumená ikona + jedna věta. */
              <PrazdnyStav ikona={BellOff} text={T.empty} testid="notif-empty" />
            ) : (
              <>
                {/* CO VIDÍ TVŮJ MANAŽER — jednorázově, nahoře, ať je opravdu
                    vidět hned po otevření (po zobrazení se zapisuje). */}
                {ukazInformaci && (
                  <div data-testid="notif-group-informace" className="px-3.5 py-2.5" style={{ borderBottom: "1px solid var(--line)" }}>
                    <div className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--info)")}><Eye size={14} /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-maly font-semibold" style={{ color: "var(--ink)" }}>{t("vykonTymu.informace.zvonecekNadpis")}</div>
                        <p className="text-drobny mt-0.5" style={{ color: "var(--muted)" }} data-testid="notif-informace-veta">{t("vykonTymu.informace.zvonecek")}</p>
                        <button
                          type="button"
                          data-testid="notif-informace-rozumim"
                          onClick={() => { if (informaceChybi) zapisInformaci(); setInformaceSkryta(true); }}
                          className="mt-1.5 inline-flex items-center min-h-[44px] px-3 rounded-lg text-maly font-semibold"
                          style={{ color: "var(--accent)", background: "var(--accent-soft)" }}
                        >
                          {t("vykonTymu.informace.rozumim")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* PO TERMÍNU */}
                {overdue.length > 0 && (
                  <div data-testid="notif-group-overdue">
                    <GroupHead label={T.overdue} count={overdue.length} color="var(--cat-c)" />
                    {overdue.map((it) => {
                      const ty = taskType(it.task);
                      const Ico = TASK_ICON[ty] || ClipboardList;
                      return (
                        <button key={it.id} onClick={() => goTask(it)} data-testid={`notif-item-overdue-${it.id}`}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--cat-c)")}><Ico size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{it.name || it.task.text || T.task}</span>
                            <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>{it.name ? (it.task.text || T.task) : ""}</span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={timeChip(it.days)}>{relLabel(it.days, lang)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* DNES */}
                {todayList.length > 0 && (
                  <div data-testid="notif-group-today">
                    <GroupHead label={T.today} count={todayList.length} color="var(--accent)" />
                    {todayList.map((it) => {
                      const ty = taskType(it.task);
                      const Ico = TASK_ICON[ty] || ClipboardList;
                      const hhmm = it.when.toLocaleTimeString(locale(lang), { hour: "2-digit", minute: "2-digit" });
                      return (
                        <button key={it.id} onClick={() => goTask(it)} data-testid={`notif-item-today-${it.id}`}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--accent)")}><Ico size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{it.name || it.task.text || T.task}</span>
                            <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>{it.name ? (it.task.text || T.task) : hhmm}</span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap tabular-nums" style={timeChip(0)}>{hhmm}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* NOVÁ REZERVACE — klient si sám vzal termín na /book/:slug.
                    Ukazuje se dopředu (do 30 dní), protože jinak se o zabrané
                    hodině poradce nedozví a slíbí ji někomu dalšímu. */}
                {/* POZVÁNKA DO TÝMU — třetí a poslední skupina bez termínu v minulosti.
                    Poradce se o pozvánce jinak dozví jen tak, že si sám otevře
                    Nastavení a sroluje dolů; do té doby mu mlčky vyprší a
                    manažer si myslí, že váhá. Klik vede rovnou na kartu, kde se
                    přijímá — panel sám nic nepotvrzuje, souhlas patří tam, kde
                    je vidět, komu se vydává přístup ke klientským datům. */}
                {pozvankyProPanel.length > 0 && (
                  <div data-testid="notif-group-pozvanka">
                    <GroupHead label={T.pozvanka} count={pozvankyProPanel.length} color="var(--info)" />
                    {pozvankyProPanel.map((it) => (
                      <button key={it.id} data-testid={`notif-item-pozvanka-${it.id}`}
                        onClick={() => { setOpen(false); navigate("/nastaveni#sec-manazer"); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--info)")}><Users size={14} /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>
                            {it.kdo ? T.pozvankaOd.replace("{kdo}", it.kdo) : T.pozvankaBezJmena}
                          </span>
                          <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>
                            {[it.email, T.pozvankaVyridit].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        {/* Lhůta jen když ji známe — „vyprší za null dní" nikdy. */}
                        {it.dny !== null && (
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            style={it.nalehava ? { background: "var(--accent-soft)", color: "var(--accent-ink)" } : { background: "var(--bg)", color: "var(--muted)", border: "1px solid var(--line)" }}>
                            {it.dny === 0 ? T.pozvankaDnes : T.pozvankaVyprsi.replace("{n}", String(it.dny))}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {noveL.length > 0 && (
                  <div data-testid="notif-group-nove-leady">
                    <GroupHead label={T.noveLeady} count={noveL.length} color="var(--accent)" />
                    {noveL.slice(0, 3).map((c) => (
                      <button key={c.id} onClick={() => { setOpen(false); navigateOverlaySafe(navigate, "/crm/calls?f=nove"); }} data-testid={`notif-item-lead-${c.id}`}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--accent)")}><Phone size={14} /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{[c.name, c.surname].filter(Boolean).join(" ") || c.phone || c.email}</span>
                          <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>{popisZdroje(c.lead_zdroj || c.source).popis}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {rezervace.length > 0 && (
                  <div data-testid="notif-group-rezervace">
                    <GroupHead label={T.rezervace} count={rezervace.length} color="var(--positive)" />
                    {rezervace.map((it) => {
                      const kdy = it.when.toLocaleDateString(locale(lang), { day: "numeric", month: "numeric" });
                      const hhmm = it.when.toLocaleTimeString(locale(lang), { hour: "2-digit", minute: "2-digit" });
                      return (
                        <button key={it.id} onClick={() => goTask(it)} data-testid={`notif-item-rezervace-${it.id}`}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--positive)")}><CalendarPlus size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{it.klient || it.text || T.rezervace}</span>
                            <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>
                              {[kdy, hhmm, it.sluzba].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={timeChip(it.days)}>{relLabel(it.days, lang)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* TÝMOVÁ AKCE — druhá skupina s BUDOUCÍM termínem.
                    Ukazuje se tolik dní předem, kolik nastavil manažer u akce
                    (pole `pripominka_dny`), ne podle pevného okna. */}
                {akce.length > 0 && (
                  <div data-testid="notif-group-akce">
                    <GroupHead label={T.akce} count={akce.length} color="var(--accent)" />
                    {akce.map((it) => {
                      const Ico = TASK_ICON[taskType(it.task)] || CalendarRange;
                      const col = barvaDruhu(it.druh);
                      const kdy = it.when.toLocaleDateString(locale(lang), { day: "numeric", month: "numeric" });
                      const hhmm = it.task.is_all_day
                        ? (t("auto.notificationCenter.celyDen"))
                        : it.when.toLocaleTimeString(locale(lang), { hour: "2-digit", minute: "2-digit" });
                      const misto = it.task.location || "";
                      return (
                        <button key={it.id} onClick={() => goTask(it)} data-testid={`notif-item-akce-${it.id}`}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon(col)}><Ico size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{it.text || T.task}</span>
                            <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>
                              {[kdy, hhmm, misto].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={timeChip(it.days)}>{relLabel(it.days, lang)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* KOMUNIKACE — jedna věta za dnešek, klik vede na seznam s hotovými zprávami. */}
                {komunikaceDnes && (
                  <button onClick={() => { setOpen(false); navigateOverlaySafe(navigate, "/crm/komunikace"); }} data-testid="notif-komunikace"
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon("var(--info)")}><Mail size={14} /></span>
                    <span className="min-w-0 flex-1 block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{komunikaceDnes}</span>
                  </button>
                )}

                {/* BLÍŽÍ SE = servisní radar */}
                {blizi.length > 0 && (
                  <div data-testid="notif-group-soon">
                    <GroupHead label={T.soon} count={blizi.length} color="var(--positive)" />
                    {radarShown.map((it) => {
                      const Ico = RADAR_ICON[it.icon] || RADAR_ICON[it.kind] || Wrench;
                      const col = RADAR_COLOR[it.kind] || "var(--accent)";
                      return (
                        <button key={it.id} onClick={() => goClient(it)} data-testid={`notif-item-soon-${it.id}`}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-[var(--accent-soft)]" style={{ borderBottom: "1px solid var(--line)" }}>
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={chipIcon(col)}><Ico size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[12.5px] font-semibold truncate" style={{ color: "var(--ink)" }}>{it.name}</span>
                            <span className="block text-[10.5px] truncate" style={{ color: "var(--muted)" }}>{it.title}</span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={timeChip(it.days)}>{relLabel(it.days, lang)}</span>
                        </button>
                      );
                    })}
                    {radarRest > 0 && (
                      <button onClick={() => { setOpen(false); navigateOverlaySafe(navigate, "/crm/servis-radar"); }} data-testid="notif-open-radar"
                        className="w-full flex items-center justify-center gap-1 px-3.5 py-2 text-[11px] font-semibold" style={{ color: "var(--accent)" }}>
                        + {radarRest} {T.more} · {T.openRadar} <ChevronRight size={12} />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupHead({ label, count, color }) {
  return (
    <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1 sticky top-0" style={{ background: "var(--surface)" }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="text-[10px] uppercase tracking-wider font-bold flex-1" style={{ color: "var(--muted)" }}>{label}</span>
      <span className="text-[9.5px] font-bold px-1.5 rounded-full tabular-nums" style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color }}>{count}</span>
    </div>
  );
}
